import React from 'react';
import { format } from 'date-fns';
import { PaperclipIcon } from 'lucide-react';
import { EmailKind, HPEvent, Registration } from '../types';
interface EmailPreviewProps {
  kind: EmailKind;
  event: HPEvent;
  registration: Registration;
  sentAt?: string;
}
function subjectFor(kind: EmailKind, event: HPEvent): string {
  if (kind === 'ticket') return `Your ticket, ${event.title}`;
  if (kind === 'declined') return `Update on your registration, ${event.title}`;
  return `Request received, ${event.title}`;
}
export function EmailPreview({
  kind,
  event,
  registration,
  sentAt
}: EmailPreviewProps) {
  const eventDate = format(new Date(event.date), 'EEEE d MMMM yyyy');
  return <div className="overflow-hidden rounded-xl border border-paper-line bg-white" data-id="element-17" data-mp="bo1bbo2ssj3l">
      <div className="border-b border-paper-line bg-paper-warm px-5 py-4 text-sm" data-id="element-18" data-mp="bqf41qa09j7z">
        <dl className="space-y-1.5" data-id="element-19" data-mp="31eo76acrysw7">
          <div className="flex gap-3" data-id="element-20" data-mp="2wejms98qxe0p">
            <dt className="w-16 shrink-0 text-xs uppercase tracking-[0.12em] text-ink-muted" data-id="element-21" data-mp="2wdidoabaj1lm">From</dt>
            <dd className="text-ink" data-id="element-22" data-mp="2fbsxq89lt9jg">Human Planet Events, events@human-planet.com</dd>
          </div>
          <div className="flex gap-3" data-id="element-23" data-mp="2jccuvbu6gd5l">
            <dt className="w-16 shrink-0 text-xs uppercase tracking-[0.12em] text-ink-muted" data-id="element-24" data-mp="25d3rjcr2ldj1">To</dt>
            <dd className="text-ink" data-id="element-25" data-mp="2ac7wtsavir43">
              {registration.fullName}, {registration.email}
            </dd>
          </div>
          <div className="flex gap-3" data-id="element-26" data-mp="1vn1zk9vljayu">
            <dt className="w-16 shrink-0 text-xs uppercase tracking-[0.12em] text-ink-muted" data-id="element-27" data-mp="1vm0qgay54yjr">Subject</dt>
            <dd className="font-medium text-ink" data-id="element-28" data-mp="20g4x0t3hv4kb">{subjectFor(kind, event)}</dd>
          </div>
          {sentAt && <div className="flex gap-3" data-id="element-29" data-mp="1rlq9624welpl">
              <dt className="w-16 shrink-0 text-xs uppercase tracking-[0.12em] text-ink-muted" data-id="element-30" data-mp="1rmrur0jjiqlk">Sent</dt>
              <dd className="text-ink-muted" data-id="element-31" data-mp="1cpr30x1cmvn3">{format(new Date(sentAt), "d MMM yyyy 'at' HH:mm")}</dd>
            </div>}
        </dl>
      </div>

      <div className="space-y-4 px-5 py-6 text-sm leading-relaxed text-ink-soft" data-id="element-32" data-mp="yixkxp8bzyr9">
        <p data-id="element-33" data-mp="yl0geos96k3d">Dear {registration.fullName.split(' ')[0]},</p>

        {kind === 'received' && <>
            <p data-id="element-34" data-mp="tqcv0ysjq5t0">
              Thank you for registering to attend <span className="text-ink" data-id="element-35" data-mp="otw4jxxvjfcr">{event.title}</span> on {eventDate} in{' '}
              {event.city}.
            </p>
            <p data-id="element-36" data-mp="jrx0elub8eer">
              Attendance at Human Planet events is reviewed by our team so that every room stays useful for the people
              in it. We will come back to you within two working days. If your place is confirmed, your ticket will
              arrive as a PDF attachment in that email.
            </p>
            <p className="rounded-md bg-paper-warm px-4 py-3 font-mono text-xs text-ink-muted" data-id="element-37" data-mp="12kj2vozq338b">
              Reference {registration.ticketCode}, status pending review
            </p>
          </>}

        {kind === 'ticket' && <>
            <p data-id="element-38" data-mp="3n96u6y284a2e">
              Your place at <span className="text-ink" data-id="element-39" data-mp="3nd30lp3sk1sb">{event.title}</span> is confirmed. Your ticket is attached to
              this email as a PDF.
            </p>
            <ul className="space-y-1.5 border-l-2 border-signal pl-4" data-id="element-40" data-mp="27su0wijmwd2m">
              <li data-id="element-41" data-mp="2cqdf5igc0puk">
                <span className="text-ink" data-id="element-42" data-mp="2cpu8wz01npwd">When</span> {eventDate}, {event.timeRange}
              </li>
              <li data-id="element-43" data-mp="2hinbbg81im5a">
                <span className="text-ink" data-id="element-44" data-mp="2hj6j443z7u5l">Where</span> {event.venue}, {event.city}
              </li>
              <li data-id="element-45" data-mp="2mg5fktb44d6y">
                <span className="text-ink" data-id="element-46" data-mp="2mirmqru3cqon">Ticket</span> <span className="font-mono" data-id="element-47" data-mp="2qhijgxuna6b8">{registration.ticketCode}</span>
              </li>
            </ul>
            <p data-id="element-48" data-mp="2vieml9769ofl">
              Please bring the ticket, printed or on your phone, along with photo ID. Tickets are non transferable. If
              you can no longer attend, reply to this email and we will release your place.
            </p>
          </>}

        {kind === 'declined' && <>
            <p data-id="element-49" data-mp="163883up8my1q">
              Thank you for your interest in <span className="text-ink" data-id="element-50" data-mp="2wjk4h0ugvh12">{event.title}</span>. On this occasion we are
              not able to confirm a place for you, as this session is tightly scoped and capacity is limited.
            </p>
            <p data-id="element-51" data-mp="35jywej1xrudj">
              We would be glad to keep you in mind for future Human Planet convenings that fit your work more closely.
            </p>
          </>}

        <p className="pt-2" data-id="element-52" data-mp="31gsq5betq372">
          With warm regards,
          <br data-id="element-53" data-mp="2dtuc0ke6f7cb" />
          The Human Planet Events team
        </p>
      </div>

      {kind === 'ticket' && <div className="flex items-center gap-3 border-t border-paper-line bg-paper-warm px-5 py-3" data-id="element-54" data-mp="28vj44f7fz6wq">
          <PaperclipIcon className="h-4 w-4 text-ink-muted" aria-hidden="true" data-id="element-55" data-mp="2mmxksfdvmw0m" />
          <span className="font-mono text-xs text-ink" data-id="element-56" data-mp="2htl7v2ik8dnp">{registration.ticketCode}.pdf</span>
          <span className="text-xs text-ink-muted" data-id="element-57" data-mp="1u40dlngyhtz4">148 KB, ticket</span>
        </div>}
    </div>;
}